import { useMemo, useState } from 'react';
import { Dayjs } from 'dayjs';
import { classifyRI, RiStatus } from '@leopart/calc';
import { roleLabel } from '../../utils/roleLabel';
import { useKeyboardActivate } from '../../../hooks/useKeyboardActivate';
import AthleteListModal from '../AthleteListModal/AthleteListModal';
import { RiLegend } from './RiLegend';
import { getAtRiskAthleteRows, ITeamRecoveryRow, useTeamRecovery } from './useTeamRecovery';
import { MetricCellClickParams } from '../MetricHistoryModal/metricHistory.constants';
import styles from './TeamRecoveryView.module.scss';

interface TeamRecoveryViewProps {
  teamId: string;
  /** Lunedì (YYYY-MM-DD) della settimana visualizzata — condiviso col tab Sforzo. */
  weekIso: string;
  /** I 7 giorni (lun-dom) come Dayjs, per gli header allineati allo sforzo. */
  weekDays: Dayjs[];
  onSelectAthlete?: (athleteId: string, athleteName?: string) => void;
  /** Click sulla cella-giorno (RI) → apre MetricHistoryModal */
  onCellClick?: (params: MetricCellClickParams) => void;
}

const RI_CLASS: Record<RiStatus, string> = {
  good: styles.riGood,
  normal: styles.riNormal,
  warning: styles.riWarning,
  critical: styles.riCritical,
};

const STATUS_LABEL: Record<RiStatus, string> = {
  good: 'Ottimo',
  normal: 'Normale',
  warning: 'Attenzione',
  critical: 'Critico',
};

function RecoveryRow({
  row,
  weekDays,
  onSelect,
  onCellClick,
}: {
  row: ITeamRecoveryRow;
  weekDays: Dayjs[];
  onSelect?: (athleteId: string, athleteName?: string) => void;
  onCellClick?: (params: MetricCellClickParams) => void;
}) {
  const fullName =
    [row.athlete.firstName, row.athlete.lastName].filter(Boolean).join(' ') || '—';

  return (
    <tr
      className={styles.row}
      onClick={() => onSelect?.(row.athlete.id, fullName)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect?.(row.athlete.id, fullName);
        }
      }}
    >
      <td className={styles.tdAthlete}>
        <span className={styles.athleteName}>{fullName}</span>
        {row.athlete.position && (
          <span className={styles.athleteRole}>{roleLabel(row.athlete.position)}</span>
        )}
      </td>

      {row.days.map((ri, i) => {
        const cls = ri != null ? RI_CLASS[classifyRI(ri)] : styles.missing;
        const dayIso = weekDays[i]?.format('YYYY-MM-DD');
        return (
          <td
            key={i}
            className={styles.tdDay}
            onClick={
              ri != null && dayIso
                ? (e) => {
                    // stopPropagation: la <tr> naviga al dettaglio atleta, la cella apre lo storico RI
                    e.stopPropagation();
                    onCellClick?.({
                      athleteId: row.athlete.id,
                      athleteName: fullName,
                      metric: 'ri',
                      highlightDate: dayIso,
                    });
                  }
                : undefined
            }
          >
            <div className={`${styles.cell} ${cls}`} title={ri != null ? `RI ${ri}` : 'Dato mancante'}>
              {ri != null ? ri : '·'}
            </div>
          </td>
        );
      })}

      <td className={styles.tdSummary}>
        {row.weeklyAvg != null && row.status ? (
          <span
            className={`${styles.avgBadge} ${RI_CLASS[row.status]}`}
            title={`RI medio ${row.weeklyAvg} · ${STATUS_LABEL[row.status]}`}
          >
            {row.weeklyAvg}
          </span>
        ) : (
          <span className={styles.avgEmpty}>–</span>
        )}
      </td>
    </tr>
  );
}

/**
 * Vista Recovery dentro il tab Sforzo: heatmap RI (0-100) atleti × 7 giorni,
 * sulla STESSA settimana dello sforzo. Serve a leggere l'impatto del recupero
 * sul carico (recovery↔sforzo) senza cambiare pagina.
 */
export function TeamRecoveryView({
  teamId,
  weekIso,
  weekDays,
  onSelectAthlete,
  onCellClick,
}: TeamRecoveryViewProps) {
  const { rows, kpi, isLoading, isError, refetch } = useTeamRecovery(teamId, weekIso, true);
  const [isAtRiskModalOpen, setIsAtRiskModalOpen] = useState(false);

  const hasAtRisk = (kpi?.atRiskCount ?? 0) > 0;
  const atRiskAthletes = useMemo(() => getAtRiskAthleteRows(rows), [rows]);
  const openAtRiskModal = hasAtRisk ? () => setIsAtRiskModalOpen(true) : undefined;
  const atRiskActivateProps = useKeyboardActivate(openAtRiskModal);

  return (
    <div className={styles.root}>
      <div className={styles.kpiRow}>
        <div className={styles.kpiCard}>
          <div className={styles.kpiLabelRow}>
            <p className={styles.kpiLabel}>RI medio squadra</p>
          </div>
          <p className={styles.kpiValue}>{kpi?.avgRi ?? '–'}</p>
        </div>
        <div
          className={`${styles.kpiCard} ${hasAtRisk ? styles.kpiCardClickable : ''}`}
          onClick={openAtRiskModal}
          {...atRiskActivateProps}
        >
          <p className={styles.kpiLabel}>Atleti a rischio</p>
          <p className={`${styles.kpiValue} ${hasAtRisk ? styles.kpiValueAlert : ''}`}>
            {kpi?.atRiskCount ?? '–'}
          </p>
        </div>
      </div>

      {isError ? (
        <div className={styles.stateBox}>
          <p className={styles.stateText}>Impossibile caricare la recovery del team.</p>
          <button className={styles.retryBtn} onClick={() => refetch()}>
            Riprova
          </button>
        </div>
      ) : !isLoading && rows.length === 0 ? (
        <div className={styles.stateBox}>
          <p className={styles.stateText}>Nessun dato recovery per questa settimana.</p>
        </div>
      ) : (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.thAthlete}>Atleta</th>
                {weekDays.map((d) => (
                  <th key={d.format('YYYY-MM-DD')} className={styles.thDay}>
                    <span className={styles.thDayName}>{d.format('ddd D')}</span>
                  </th>
                ))}
                <th className={styles.thSummary}>RI medio</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className={styles.stateText}>
                    Caricamento…
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <RecoveryRow
                    key={row.athlete.id}
                    row={row}
                    weekDays={weekDays}
                    onSelect={onSelectAthlete}
                    onCellClick={onCellClick}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <RiLegend />

      <AthleteListModal
        isOpen={isAtRiskModalOpen}
        onClose={() => setIsAtRiskModalOpen(false)}
        title="Atleti a rischio"
        emptyMessage="Nessun atleta a rischio questa settimana."
        athletes={atRiskAthletes}
      />
    </div>
  );
}

export default TeamRecoveryView;
